import Keycloak from 'keycloak-js';
import {resolveMigratedApiUrl} from '../api/MigratedApiRouting.js';
import { logAuthEvent } from '../monitoring/securityMonitoring.js';
import {clearAuthStatus, publishAuthStatus, publishSessionExpired, suppressAuthStatusForRequest} from './authStatus.js';

const defaultConfig = {
    url: 'http://localhost:8080',
    realm: 'Folio-dev',
    clientId: 'folio-dev',
};

const runtimeConfig = window.UXFOLIO_AUTH_CONFIG || defaultConfig;
const bffAuthConfig = window.UXFOLIO_BFF_AUTH_CONFIG || {
    enabled: false,
    baseUrl: '',
};
const keycloak = new Keycloak(runtimeConfig);

let initPromise = null;
let authMigrationPromise = null;
let sessionInvalidationPromise = null;
let sessionValidationPromise = null;
let lastAuthCheckResult = {
    status: 'idle',
    error: null,
};

const authStorageVersion = 'pkce-jwks-v7-force-cookie-purge';
const authStorageVersionKey = 'uxfolio-auth-version';
const authStorageKeyFragments = [
    'keycloak',
    'kc-token',
    'kc-callback',
    'pkce',
    'oauth',
    'oidc',
    'uxfolio-auth',
];
const authCookieNames = [
    'JSESSIONID',
    'SESSION',
    'XSRF-TOKEN',
    'remember-me',
];
const authCookieKeyFragments = [
    'uxfolio-auth',
];

const authCallbackParams = [
    'code',
    'error',
    'error_description',
    'error_uri',
    'iss',
    'session_state',
    'state',
];

const protectedRoutePrefixes = [
    '/admin',
    '/edu',
    '/home',
    '/payment',
    '/projects',
    '/settings',
    '/jobfitchecker',
    '/myreviews',
];

const editorViewModeSessionKey = 'uxfolio_editor_view_mode';
const editorViewModeSessionTtlMs = 30 * 60 * 1000;

const socialLoginProviderLabels = {
    google: 'Google',
    facebook: 'Facebook',
};

const legacyUserSessionFailureStatuses = new Set([401, 400]);
const authRequestMetadataKey = '__uxfolioAuthRequest';

class BffAuthRequestError extends Error {
    constructor(message, status, data = null) {
        super(message);
        this.name = 'BffAuthRequestError';
        this.status = status;
        this.data = data;
    }
}

function shouldUseBffAuth() {
    return Boolean(bffAuthConfig.enabled);
}

function resolveRequestUrl(url) {
    try {
        return new URL(url || '', window.location.origin);
    } catch (error) {
        return null;
    }
}

function isSameOriginApiRequest(requestUrl) {
    return requestUrl?.origin === window.location.origin && requestUrl.pathname.startsWith('/api/');
}

export function isBffAuthEnabled() {
    return shouldUseBffAuth();
}

function getBffAuthBaseUrl() {
    return (bffAuthConfig.baseUrl || '').replace(/\/$/, '');
}

function buildBffAuthUrl(path) {
    return `${getBffAuthBaseUrl()}/api/auth${path}`;
}

function getSocialLoginProviderLabel(provider) {
    return socialLoginProviderLabels[provider] || provider;
}

async function requestBffAuth(path, options = {}) {
    const response = await fetch(buildBffAuthUrl(path), {
        ...options,
        credentials: 'include',
        headers: {
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...(options.headers || {}),
        },
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new BffAuthRequestError(data.error || 'Authentication failed', response.status, data);
    }

    return data;
}

function decodeBase64UrlJson(value) {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded));
}

function decodeJwtPayload(token) {
    const [, payload] = token.split('.');
    if (!payload) {
        return {};
    }

    return decodeBase64UrlJson(payload);
}

function applyBffAuthResult(result) {
    keycloak.authenticated = true;
    keycloak.token = result.accessToken;
    keycloak.refreshToken = null;
    keycloak.idToken = null;
    keycloak.tokenParsed = decodeJwtPayload(result.accessToken);
    keycloak.refreshTokenParsed = null;
    keycloak.idTokenParsed = null;

    lastAuthCheckResult = { status: 'authenticated', error: null };
    clearAuthStatus();
}

function getHostname(value) {
    try {
        return new URL(value).hostname;
    } catch (error) {
        return '';
    }
}

function isLocalHostname(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function isUxfolioHostname(hostname) {
    return hostname === 'uxfol.io' || hostname.endsWith('.uxfol.io');
}

function shouldUseSilentCheckSso() {
    const appHostname = window.location.hostname;
    const authHostname = getHostname(runtimeConfig.url);

    if (!authHostname) {
        return false;
    }

    if (appHostname === authHostname) {
        return true;
    }

    if (isLocalHostname(appHostname) && !isLocalHostname(authHostname)) {
        return true;
    }

    if (isLocalHostname(authHostname)) {
        return false;
    }

    return isUxfolioHostname(appHostname) && isUxfolioHostname(authHostname);
}

function getParamNames(value = '') {
    const rawValue = String(value || '');
    const withoutHash = rawValue.includes('#') ? rawValue.slice(0, rawValue.indexOf('#')) : rawValue;
    const queryStart = withoutHash.indexOf('?');
    const rawParams = queryStart >= 0
        ? withoutHash.slice(queryStart + 1)
        : withoutHash.replace(/^[?#]/, '');

    if (!rawParams) {
        return new Set();
    }

    try {
        return new Set([...new URLSearchParams(rawParams).keys()]);
    } catch (error) {
        return new Set();
    }
}

function hasAuthCallbackParams(value = '') {
    const paramNames = getParamNames(value);
    return authCallbackParams.some((param) => paramNames.has(param));
}

function clearMatchingStorage(storage) {
    if (!storage) {
        return;
    }

    Object.keys(storage).forEach((key) => {
        const normalizedKey = key.toLowerCase();
        if (authStorageKeyFragments.some((fragment) => normalizedKey.includes(fragment))) {
            storage.removeItem(key);
        }
    });
}

function clearFrontendAuthState() {
    clearMatchingStorage(window.localStorage);
    clearMatchingStorage(window.sessionStorage);
}

function getCookieDomainCandidates(hostname = window.location.hostname) {
    const candidates = [''];

    if (!isLocalHostname(hostname) && hostname.includes('.')) {
        candidates.push(hostname, `.${hostname}`);

        const parts = hostname.split('.');
        if (parts.length > 2) {
            const parentDomain = parts.slice(-2).join('.');
            candidates.push(parentDomain, `.${parentDomain}`);
        }
    }

    return [...new Set(candidates)];
}

function getCookiePathCandidates(pathname = window.location.pathname) {
    const candidates = ['/'];
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';

    segments.forEach((segment) => {
        currentPath = `${currentPath}/${segment}`;
        candidates.push(currentPath);
    });

    return [...new Set(candidates)];
}

function shouldClearAuthCookie(cookieName) {
    const normalizedCookieName = cookieName.toLowerCase();

    return authCookieNames.some((name) => name.toLowerCase() === normalizedCookieName)
        || authCookieKeyFragments.some((fragment) => normalizedCookieName.includes(fragment));
}

function expireCookie(cookieName, domain, path) {
    const attributes = [
        `${cookieName}=`,
        'Max-Age=0',
        'expires=Thu, 01 Jan 1970 00:00:00 GMT',
        `path=${path}`,
    ];

    if (domain) {
        attributes.push(`domain=${domain}`);
    }

    if (window.location.protocol === 'https:') {
        attributes.push('Secure');
    }

    document.cookie = attributes.join('; ');
}

function clearLegacyAuthCookies() {
    if (!document.cookie) {
        return;
    }

    const cookieNames = document.cookie
        .split(';')
        .map((cookie) => cookie.split('=')[0].trim())
        .filter(Boolean)
        .filter(shouldClearAuthCookie);

    const domains = getCookieDomainCandidates();
    const paths = getCookiePathCandidates();

    [...new Set(cookieNames)].forEach((cookieName) => {
        domains.forEach((domain) => {
            paths.forEach((path) => expireCookie(cookieName, domain, path));
        });
    });
}

function clearLegacyBrowserAuthState() {
    clearFrontendAuthState();
    clearLegacyAuthCookies();
}

export function getLastAuthCheckResult() {
    return lastAuthCheckResult;
}

function resetKeycloakMemoryState() {
    keycloak.authenticated = false;
    keycloak.token = null;
    keycloak.refreshToken = null;
    keycloak.idToken = null;
    keycloak.tokenParsed = null;
    keycloak.refreshTokenParsed = null;
    keycloak.idTokenParsed = null;
}

function markCurrentAuthVersion() {
    window.localStorage?.setItem(authStorageVersionKey, authStorageVersion);
}

function trimTrailingSlash(value) {
    return value?.replace(/\/$/, '') ?? '';
}

function buildKeycloakLogoutUrl(redirectUri) {
    const authServerUrl = trimTrailingSlash(runtimeConfig.url);
    const logoutUrl = new URL(`${authServerUrl}/realms/${runtimeConfig.realm}/protocol/openid-connect/logout`, window.location.origin);
    logoutUrl.searchParams.set('client_id', runtimeConfig.clientId);
    logoutUrl.searchParams.set('post_logout_redirect_uri', redirectUri);

    if (keycloak.idToken) {
        logoutUrl.searchParams.set('id_token_hint', keycloak.idToken);
    }

    return logoutUrl.toString();
}

async function clearLegacyServerSession() {
    try {
        const response = await fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            cache: 'no-store',
        });

        if (!response.ok) {
            console.warn('Legacy auth session cleanup returned an unexpected status', response.status);
            return false;
        }

        return true;
    } catch (error) {
        console.warn('Legacy auth session cleanup failed', error);
        return false;
    }
}

export async function migrateLegacyAuthState() {
    if (!authMigrationPromise) {
        authMigrationPromise = (async () => {
            if (isAuthCallbackUrl()) {
                return false;
            }

            const currentVersion = window.localStorage?.getItem(authStorageVersionKey);

            if (currentVersion === authStorageVersion) {
                return false;
            }

            clearLegacyBrowserAuthState();
            await clearLegacyServerSession();
            markCurrentAuthVersion();

            return true;
        })();
    }

    try {
        return await authMigrationPromise;
    } finally {
        authMigrationPromise = null;
    }
}

export async function resetAuthState() {
    if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        tokenRefreshTimer = null;
    }
    
    resetKeycloakMemoryState();
    initPromise = null;
    
    clearLegacyBrowserAuthState();
    await clearLegacyServerSession();
    markCurrentAuthVersion();
}

export function isAuthCallbackUrl(search = window.location.search, hash = window.location.hash) {
    return hasAuthCallbackParams(search) || hasAuthCallbackParams(hash);
}

export function shouldInitializeAuth(pathname = window.location.pathname, search = window.location.search) {
    return isAuthCallbackUrl(search, window.location.hash)
        || requiresAuthentication(pathname)
        || isEditorViewModeRoute(pathname)
        || isOwnerPreviewRoute(pathname)
        || pathname.startsWith('/review');
}

// The owner's draft preview, shown inside the phone editor's iframe. Its data needs the owner's session.
export function isOwnerPreviewRoute(pathname = window.location.pathname) {
    return pathname.startsWith('/preview/');
}

export function requiresAuthentication(pathname = window.location.pathname) {
    return protectedRoutePrefixes.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function markEditorViewMode(pathname = window.location.pathname) {
    try {
        window.sessionStorage?.setItem(editorViewModeSessionKey, JSON.stringify({
            pathname,
            expiresAt: Date.now() + editorViewModeSessionTtlMs,
        }));
    } catch (error) {
        console.warn('Failed to mark editor view mode session', error);
    }
}

export function clearEditorViewMode() {
    try {
        window.sessionStorage?.removeItem(editorViewModeSessionKey);
    } catch (error) {
        console.warn('Failed to clear editor view mode session', error);
    }
}

export function isEditorViewModeRoute(pathname = window.location.pathname) {
    try {
        const rawSession = window.sessionStorage?.getItem(editorViewModeSessionKey);
        if (!rawSession) return false;

        const session = JSON.parse(rawSession);
        const isExpired = !session.expiresAt || session.expiresAt < Date.now();
        const matchesPath = session.pathname === pathname;

        if (isExpired || !matchesPath) {
            window.sessionStorage?.removeItem(editorViewModeSessionKey);
            return false;
        }

        return true;
    } catch (error) {
        window.sessionStorage?.removeItem(editorViewModeSessionKey);
        return false;
    }
}

export function sanitizeAuthRedirect(redirectUri = window.location.href) {
    const url = new URL(redirectUri, window.location.origin);

    authCallbackParams.forEach((param) => url.searchParams.delete(param));

    if (hasAuthCallbackParams(url.hash)) {
        url.hash = '';
    }

    return url.toString();
}

export function buildAuthRedirect(path = '/home', params = {}) {
    const url = new URL(path, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            url.searchParams.set(key, value);
        }
    });

    return url.toString();
}

let tokenRefreshTimer = null;
let bffSessionCheckPromise = null;

const keycloakTokenRefreshLeewaySeconds = 60;
const bffRequestRefreshLeewaySeconds = 30;

const getKeycloakTokenRefreshDelayMs = (expiresIn) =>
    Math.max(0, (expiresIn - keycloakTokenRefreshLeewaySeconds) * 1000);

function scheduleTokenRefresh() {
    if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        tokenRefreshTimer = null;
    }

    if (shouldUseBffAuth()) {
        return;
    }
    
    if (!keycloak.authenticated || !keycloak.tokenParsed?.exp) {
        return;
    }
    
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = keycloak.tokenParsed.exp - now;
    
    const refreshIn = getKeycloakTokenRefreshDelayMs(expiresIn);
    
    tokenRefreshTimer = setTimeout(async () => {
        try {
            const refreshed = await keycloak.updateToken(-1);
            
            if (refreshed) {
                const expiresIn = keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000);
                
                logAuthEvent('token_refresh_success', {
                    userId: keycloak.tokenParsed?.sub,
                    expiresIn
                });

            }
            scheduleTokenRefresh();
        } catch (error) {
            logAuthEvent('token_refresh_failure', {
                userId: keycloak.tokenParsed?.sub,
                error: error.message || 'Unknown error'
            });

            await handleSessionValidationFailure(error, { error: 'Token refresh failed' });
        }
    }, refreshIn);
}

function initializeKeycloak(onLoad = 'check-sso') {
    if (!initPromise) {
        const prepareInit = migrateLegacyAuthState();
        const initOptions = {
            pkceMethod: 'S256',
            checkLoginIframe: false,
        };

        const shouldShowAuthStatus = requiresAuthentication(window.location.pathname)
            || isAuthCallbackUrl(window.location.search, window.location.hash);

        if (onLoad) {
            initOptions.onLoad = onLoad;
        }

        if (onLoad === 'check-sso' && shouldUseSilentCheckSso()) {
            initOptions.silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso.html`;
            initOptions.silentCheckSsoFallback = true;
        }

        if (onLoad === 'check-sso' && shouldShowAuthStatus) {
            lastAuthCheckResult = { status: 'checking', error: null };
            publishAuthStatus({
                state: 'checking_sso',
                title: 'Checking your session',
                message: 'We are looking for your UXfolio session so you can continue without signing in again.',
                busy: true,
            });
        }

        initPromise = prepareInit
            .then(() => keycloak.init(initOptions))
            .then((authenticated) => {
                if (authenticated && keycloak.token) {
                    clearFrontendAuthState();
                    markCurrentAuthVersion();
                    lastAuthCheckResult = { status: 'authenticated', error: null };
                    clearAuthStatus();
                    
                    logAuthEvent('auth_success', {
                        userId: keycloak.tokenParsed?.sub,
                        email: keycloak.tokenParsed?.email,
                        expiresIn: keycloak.tokenParsed?.exp ? (keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000)) : null
                    });
                    
                    scheduleTokenRefresh();
                } else if (onLoad === 'check-sso') {
                    lastAuthCheckResult = { status: 'no_sso_session', error: null };
                    if (shouldShowAuthStatus) {
                        publishAuthStatus({
                            state: 'redirecting_login',
                            title: 'Opening secure login',
                            message: 'We could not find an active UXfolio session, so we are taking you to sign in.',
                            busy: true,
                        });
                    }
                }
                
                return authenticated;
            })
            .catch((error) => {
                logAuthEvent('auth_failure', {
                    error: error.message || 'Unknown error',
                    errorType: error.error || 'init_error'
                });
                
                initPromise = null;
                lastAuthCheckResult = { status: 'error', error };
                if (shouldShowAuthStatus) {
                    publishAuthStatus({
                        state: 'auth_check_failed',
                        title: 'We could not check your session',
                        message: 'Your work is still safe. You can try again or open the secure login page directly.',
                        primaryLabel: 'Try again',
                        primaryAction: 'retry',
                        secondaryLabel: 'Open login',
                        secondaryAction: 'forceLogin',
                        redirectUri: window.location.href,
                    });
                }
                return false;
            });
    }

    return initPromise;
}

export function initAuth() {
    if (keycloak.authenticated && keycloak.token) {
        return Promise.resolve(true);
    }

    if (shouldUseBffAuth()) {
        return initializeBffAuth('check-sso');
    }
    
    return initializeKeycloak('check-sso').then((authenticated) => {
        if (authenticated && keycloak.authenticated) {
            scheduleTokenRefresh();
        }
        return authenticated;
    });
}

function initializeForRedirect() {
    return initializeKeycloak(null);
}

function redirectAuthenticatedUser(redirectUri) {
    publishAuthStatus({
        state: 'signed_in',
        title: 'Welcome back',
        message: 'We found your UXfolio session and are taking you back to your workspace.',
        busy: true,
    });
    window.location.assign(redirectUri);
}

async function checkBffSession() {
    if (!bffSessionCheckPromise) {
        bffSessionCheckPromise = requestBffAuth('/check-sso')
            .finally(() => {
                bffSessionCheckPromise = null;
            });
    }

    const result = await bffSessionCheckPromise;

    if (!result.authenticated || !result.accessToken) {
        throw new BffAuthRequestError('No active BFF session', 401, result);
    }

    applyBffAuthResult(result);
    return true;
}

function getAuthErrorStatus(error) {
    return error?.response?.status || error?.status || null;
}

function isConfirmedSessionLoss(error) {
    if (shouldUseBffAuth()) {
        return error instanceof BffAuthRequestError && error.status === 401;
    }

    // keycloak-js clears its local token only when the refresh endpoint rejects
    // the refresh token (currently with HTTP 400). Network and server failures
    // leave the token in place and are not proof that the session has expired.
    return !keycloak.authenticated || !keycloak.token || getAuthErrorStatus(error) === 400;
}

function isCurrentAuthRequest(authRequest) {
    if (!authRequest || !keycloak.authenticated) {
        return false;
    }

    if (authRequest.sessionToken && authRequest.sessionToken !== keycloak.token) {
        return false;
    }

    return authRequest.bff || Boolean(authRequest.attachedToken);
}

function belongsToCurrentOrClearedSession(authRequest) {
    return !authRequest?.sessionToken
        || !keycloak.token
        || authRequest.sessionToken === keycloak.token;
}

async function invalidateConfirmedSession(detail, authRequest = null) {
    if (!belongsToCurrentOrClearedSession(authRequest)) {
        return false;
    }

    if (!sessionInvalidationPromise) {
        sessionInvalidationPromise = (async () => {
            await resetAuthState();
            publishSessionExpired(detail);
        })().finally(() => {
            sessionInvalidationPromise = null;
        });
    }

    await sessionInvalidationPromise;
    return true;
}

async function handleSessionValidationFailure(error, detail, authRequest = null) {
    if (!isConfirmedSessionLoss(error)) {
        return false;
    }

    return invalidateConfirmedSession(detail, authRequest);
}

async function validateSessionAfterUnauthorized() {
    if (!sessionValidationPromise) {
        sessionValidationPromise = (async () => {
            try {
                if (shouldUseBffAuth()) {
                    await checkBffSession();
                } else {
                    await keycloak.updateToken(-1);
                }

                return { valid: true };
            } catch (error) {
                return {
                    valid: false,
                    confirmedInvalid: isConfirmedSessionLoss(error),
                    error,
                };
            }
        })().finally(() => {
            sessionValidationPromise = null;
        });
    }

    return sessionValidationPromise;
}

function initializeBffAuth(onLoad = 'check-sso') {
    if (!initPromise) {
        const prepareInit = migrateLegacyAuthState();

        // Only show the auth status modal (e.g. "Checking session...") if we are on a protected route
        // or a login callback. This prevents blocking public review pages (guest review flow) with auth modals.
        const shouldShowAuthStatus = requiresAuthentication(window.location.pathname)
            || isAuthCallbackUrl(window.location.search, window.location.hash);

        if (onLoad === 'check-sso' && shouldShowAuthStatus) {
            lastAuthCheckResult = { status: 'checking', error: null };
            publishAuthStatus({
                state: 'checking_sso',
                title: 'Checking your session',
                message: 'We are looking for your UXfolio session so you can continue without signing in again.',
                busy: true,
            });
        }

        initPromise = prepareInit
            .then(() => checkBffSession())
            .catch((error) => {
                initPromise = null;

                if (error instanceof BffAuthRequestError && error.status === 401) {
                    lastAuthCheckResult = { status: 'no_sso_session', error: null };

                    if (onLoad === 'check-sso' && shouldShowAuthStatus) {
                        publishAuthStatus({
                            state: 'redirecting_login',
                            title: 'Opening secure login',
                            message: 'We could not find an active UXfolio session, so we are taking you to sign in.',
                            busy: true,
                        });
                    }

                    return false;
                }

                logAuthEvent('auth_failure', {
                    error: error.message || 'Unknown error',
                    errorType: 'bff_session_check_error'
                });

                lastAuthCheckResult = { status: 'error', error };
                if (shouldShowAuthStatus) {
                    publishAuthStatus({
                        state: 'auth_check_failed',
                        title: 'We could not check your session',
                        message: 'Your work is still safe. You can try again or open the secure login page directly.',
                        primaryLabel: 'Try again',
                        primaryAction: 'retry',
                        secondaryLabel: 'Open login',
                        secondaryAction: 'forceLogin',
                        redirectUri: window.location.href,
                    });
                }
                return false;
            });
    }

    return initPromise;
}

export async function getAccessToken() {
    if (shouldUseBffAuth()) {
        if (!keycloak.authenticated) {
            await initAuth();
        }

        if (!keycloak.authenticated) {
            return null;
        }

        const expiresAt = keycloak.tokenParsed?.exp || 0;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt - now <= bffRequestRefreshLeewaySeconds) {
            try {
                await checkBffSession();
            } catch (error) {
                console.error('BFF session refresh failed', error);
                await handleSessionValidationFailure(error, { error: 'BFF session refresh failed' });
                return null;
            }
        }

        return keycloak.token || null;
    }

    if (!keycloak.authenticated) {
        await initAuth();
    }

    if (!keycloak.authenticated) {
        return null;
    }

    try {
        await keycloak.updateToken(10);
    } catch (error) {
        console.error('Keycloak token refresh failed', error);
        await handleSessionValidationFailure(error, { error: 'Keycloak token refresh failed' });
        return null;
    }

    return keycloak.token || null;
}

export async function login(redirectUri = window.location.href, options = {}) {
    const sanitizedRedirectUri = sanitizeAuthRedirect(redirectUri);

    if (keycloak.authenticated && keycloak.token) {
        redirectAuthenticatedUser(sanitizedRedirectUri);
        return;
    }

    if (shouldUseBffAuth()) {
        if (!options.skipSessionCheck) {
            const authenticated = await initAuth();
            const result = getLastAuthCheckResult();

            if (authenticated && keycloak.authenticated) {
                redirectAuthenticatedUser(sanitizedRedirectUri);
                return;
            }

            if (result.status === 'error') {
                return;
            }
        }

        window.location.assign(`/login?redirectUri=${encodeURIComponent(sanitizedRedirectUri)}`);
        return;
    }

    if (!options.skipSessionCheck && !keycloak.authenticated) {
        const authenticated = await initAuth();
        const result = getLastAuthCheckResult();

        if (authenticated && keycloak.authenticated) {
            redirectAuthenticatedUser(sanitizedRedirectUri);
            return;
        }

        if (result.status === 'error') {
            return;
        }
    } else if (!initPromise) {
        await initializeForRedirect();
    }

    try {
        publishAuthStatus({
            state: 'redirecting_login',
            title: 'Opening secure login',
            message: 'You are being redirected to UXfolio sign in.',
            busy: true,
        });

        return keycloak.login({ redirectUri: sanitizedRedirectUri });
    } catch (error) {
        console.error('[Login] Could not redirect to Keycloak', error);
        publishAuthStatus({
            state: 'login_redirect_failed',
            title: 'We could not open secure login',
            message: 'Something interrupted the redirect. You can try again, or reload the page before signing in.',
            primaryLabel: 'Try again',
            primaryAction: 'forceLogin',
            secondaryLabel: 'Reload page',
            secondaryAction: 'retry',
            redirectUri: sanitizedRedirectUri,
        });
    }
}

export async function register(redirectUri = window.location.href) {
    if (shouldUseBffAuth()) {
        window.location.assign(`/signup?redirectUri=${encodeURIComponent(sanitizeAuthRedirect(redirectUri))}`);
        return;
    }

    await initializeForRedirect();
    return keycloak.register({ redirectUri: sanitizeAuthRedirect(redirectUri) });
}

export async function loginWithPassword(credentials) {
    const result = await requestBffAuth('/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
    });

    applyBffAuthResult(result);
    return result;
}

export async function signupWithPassword(payload) {
    const result = await requestBffAuth('/signup', {
        method: 'POST',
        body: JSON.stringify(payload),
    });

    applyBffAuthResult(result);
    return result;
}

export async function requestPasswordReset(email) {
    if (!shouldUseBffAuth()) {
        throw new BffAuthRequestError('Password reset is only available through UXfolio secure login.', 400);
    }

    const result = await requestBffAuth('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });

    logAuthEvent('forgot_password_requested', { email });
    return result;
}

export async function resetPassword(payload) {
    if (!shouldUseBffAuth()) {
        throw new BffAuthRequestError('Password reset is only available through UXfolio secure login.', 400);
    }

    const result = await requestBffAuth('/reset-password', {
        method: 'POST',
        body: JSON.stringify(payload),
    });

    logAuthEvent('password_reset_completed');
    return result;
}

export async function startSocialLogin(provider, redirectUri = window.location.href) {
    const sanitizedRedirectUri = sanitizeAuthRedirect(redirectUri);
    const providerLabel = getSocialLoginProviderLabel(provider);

    publishAuthStatus({
        state: 'redirecting_login',
        title: 'Opening secure login',
        message: `We are opening ${providerLabel} through UXfolio secure login.`,
        busy: true,
    });

    logAuthEvent('social_login_start', { provider });

    try {
        if (shouldUseBffAuth()) {
            const result = await requestBffAuth(
                `/social/${encodeURIComponent(provider)}/start?redirectUri=${encodeURIComponent(sanitizedRedirectUri)}`,
            );

            if (!result.redirectUrl) {
                throw new BffAuthRequestError('Secure login did not return a redirect URL', 502, result);
            }

            window.location.assign(result.redirectUrl);
            return result;
        }

        await initializeForRedirect();
        return keycloak.login({
            redirectUri: sanitizedRedirectUri,
            idpHint: provider,
        });
    } catch (error) {
        logAuthEvent('social_login_start_failure', {
            provider,
            error: error.message || 'Unknown error',
        });

        publishAuthStatus({
            state: 'login_redirect_failed',
            title: 'We could not open secure login',
            message: `Something interrupted ${providerLabel} sign in. You can try again or use email and password.`,
            primaryLabel: 'Try again',
            primaryAction: 'forceLogin',
            secondaryLabel: 'Reload page',
            secondaryAction: 'retry',
            redirectUri: sanitizedRedirectUri,
        });

        throw error;
    }
}

export function loginTo(path = '/home', params = {}) {
    return login(buildAuthRedirect(path, params));
}

export function registerTo(path = '/home/onboarding', params = {}) {
    return register(buildAuthRedirect(path, params));
}

export async function logout(redirectUri = window.location.origin) {
    const sanitizedRedirectUri = sanitizeAuthRedirect(redirectUri);
    let keycloakLogoutUrl = null;

    publishAuthStatus({
        state: 'logging_out',
        title: 'Signing you out',
        message: 'We are closing your UXfolio session and signing you out from secure login.',
        busy: true,
    });

    if (!shouldUseBffAuth()) {
        try {
            keycloakLogoutUrl = buildKeycloakLogoutUrl(sanitizedRedirectUri);
        } catch (error) {
            console.error('[Logout] Could not build Keycloak logout URL', error);
        }
    }

    if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        tokenRefreshTimer = null;
    }

    if (shouldUseBffAuth()) {
        await requestBffAuth('/logout', { method: 'POST' }).catch((error) => {
            console.warn('BFF auth session cleanup failed', error);
        });
    }

    await clearLegacyServerSession();
    clearLegacyBrowserAuthState();
    resetKeycloakMemoryState();
    markCurrentAuthVersion();

    if (shouldUseBffAuth()) {
        window.location.assign(sanitizedRedirectUri);
        return;
    }

    if (keycloakLogoutUrl) {
        window.location.assign(keycloakLogoutUrl);
        return;
    }

    publishAuthStatus({
        state: 'logout_local_only',
        title: 'Signed out on this device',
        message: 'We cleared your local UXfolio session, but could not reach secure login logout automatically.',
        primaryLabel: 'Try secure logout again',
        primaryAction: 'retryLogout',
        secondaryLabel: 'Go to homepage',
        secondaryAction: 'goHome',
    });
}

export function isValidBearerToken(token) {
    if (!token || typeof token !== 'string') return false;
    if (/[^a-zA-Z0-9\-._~+/=]/.test(token)) return false;
    const segments = token.split('.');
    if (segments.length !== 3) return false;
    return segments.every(s => s.length > 0);
}

export function installAuthInterceptor(axiosInstance) {
    axiosInstance.interceptors.request.use(async (config) => {
        config.url = resolveMigratedApiUrl(config.url || '');
        const requestUrl = resolveRequestUrl(config.url);

        if (!isSameOriginApiRequest(requestUrl)) {
            return config;
        }

        const shouldUseAuthenticatedApi = keycloak.authenticated
            || shouldInitializeAuth()
            || requiresAuthentication(window.location.pathname);

        if (!shouldUseAuthenticatedApi) {
            return config;
        }

        let token = await getAccessToken();

        if (token) {
            if (!isValidBearerToken(token)) {
                keycloak.authenticated = false;
                keycloak.token = null;
                token = await getAccessToken();
                if (!token) {
                    return config;
                }
            }

            const attachedToken = keycloak.token || token;
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${attachedToken}`;
            config[authRequestMetadataKey] = {
                attachedToken,
                sessionToken: attachedToken,
                bff: shouldUseBffAuth(),
            };
        } else if (shouldUseBffAuth() && keycloak.authenticated) {
            // BFF deployments may authenticate an API request with their session
            // cookie rather than an Authorization header.
            config[authRequestMetadataKey] = {
                attachedToken: null,
                sessionToken: keycloak.token || null,
                bff: true,
            };
        }

        return config;
    });

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status;
            const isUnauthorized = status === 401;
            const isReviewFlow = window.location.pathname.includes('/review');
            const requestUrl = resolveRequestUrl(error.config?.url);
            const shouldSuppressAuthStatus = error.config?.skipAuthStatusHandling
                || suppressAuthStatusForRequest(requestUrl?.toString());
            const isSameOriginUserMeRequest = requestUrl && requestUrl.origin === window.location.origin && (requestUrl.pathname === '/api/users/me' || requestUrl.pathname === '/api/users/me/ensure');
            const isLegacyUserSessionFailure =
                isSameOriginUserMeRequest && status === 400 && legacyUserSessionFailureStatuses.has(status);
            const authRequest = error.config?.[authRequestMetadataKey];

            if (isLegacyUserSessionFailure && !isReviewFlow && !shouldSuppressAuthStatus) {
                logAuthEvent('unauthorized_access', {
                    url: error.config?.url,
                    method: error.config?.method,
                    status,
                    userId: keycloak.tokenParsed?.sub,
                    reason: 'Legacy Spring session cleanup triggered',
                });
                await resetAuthState();
                publishSessionExpired({ status, url: requestUrl?.toString() });
            } else if (
                isUnauthorized
                && !isReviewFlow
                && !shouldSuppressAuthStatus
                && isSameOriginApiRequest(requestUrl)
                && isCurrentAuthRequest(authRequest)
            ) {
                logAuthEvent('unauthorized_access', {
                    url: error.config?.url,
                    method: error.config?.method,
                    status,
                    userId: keycloak.tokenParsed?.sub,
                });

                const validation = await validateSessionAfterUnauthorized();
                if (validation.confirmedInvalid) {
                    await invalidateConfirmedSession(
                        { status, url: requestUrl.toString() },
                        authRequest,
                    );
                }
            }

            return Promise.reject(error);
        },
    );
}

export { keycloak };
